"use client"

import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"

import { CallCloseActions } from "@/components/marketing/save-our-number"
import { callMinutes, formatWhen } from "../youre-booked/youre-booked"
import { StandaloneClosingBand } from "./standalone"

export type WhatHappensNextCloseProps = {
  /** Fallback booking values for a render with no booking in the URL, which
   *  is the workbench cell. On the live page Cal.com puts the real uid, start
   *  and end in the URL after bookingSuccessful, and those win. */
  uid: string | null
  start: string | null
  end: string | null
}

/**
 * The post-booking close, a cell of the one closing-band module.
 *
 * His law: "The closing band aka the CTA band should contain all the CTE
 * cells from every single page And they should have the exact same UI design
 * with just a copy looking different But to come prepared Get the most out of
 * your call but all of them should be cells on one module."
 *
 * So this close renders the same band every other page closes on. Its words
 * are its own, unchanged, and its action is the add-to-calendar control
 * rather than a link, which is what the shell's action slot is for.
 */
export function WhatHappensNextClose(props: WhatHappensNextCloseProps) {
  const searchParams = useSearchParams()
  // Workbench-only fallback, same rule as the booked hero: on the live page
  // the URL is the sole source of booking data, so a bare URL hides the
  // see-you line and the calendar button instead of showing a fake date.
  const onLivePage = usePathname() === "/what-happens-next"
  const uid = searchParams.get("uid") ?? (onLivePage ? null : props.uid)
  const start = searchParams.get("start") ?? (onLivePage ? null : props.start)
  const end = searchParams.get("end") ?? (onLivePage ? null : props.end)
  const when = formatWhen(start)
  // The call is a phone call, and its length is the booking's own, the same
  // number the confirmation panel above prints. His answer on the old line
  // ("keep your camera on ... the full 30 minutes"): "Yes, match the real call".
  const minutes = callMinutes(start, end)
  const setAside = minutes ? `set aside the full ${minutes} minutes` : "set aside the time"

  return (
    <StandaloneClosingBand
      heading="Get the most out of your call"
      // Plain string pieces rather than one template, so check:landing-copy
      // fingerprints every word of the line.
      body={
        "Pick up from somewhere quiet and " +
        setAside +
        " without distractions. Before we talk, think about the one thing you'd most like to change about your dating life over the next 3-6 months."
      }
      action={
        <div className="isolate mt-1 flex flex-col items-center gap-3">
          {when ? <p className="text-[15px] font-extrabold text-white">See you {when}.</p> : null}
          <CallCloseActions
            uid={uid}
            fallbackStart={start}
            fallbackEnd={end}
            assumePhoneCall={!onLivePage}
            saveLabel="Save our number"
            reason="Many phones silence unknown callers."
          />
          {/* Reschedule sits under the calendar button here exactly as it does
              on the confirmation panel above. His ruling on seeing both: he
              prefers this one, "if you add the option to reschedule like in
              the other one", and "both should have rescheduling". */}
          <Link
            href="/book"
            // A 44px tap area with nothing on screen moving: an invisible
            // after: box centred on the words takes the tap, so the link's own
            // box, and the focus ring drawn around it, still hug the words.
            // -z-1 inside the isolated column keeps the words painting under
            // the Add to calendar button's shadow, as they did before.
            className="relative -z-1 text-[0.8125rem] text-[#9ca3af] underline underline-offset-4 transition-colors after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-[''] hover:text-white"
          >
            Can&apos;t make it? Reschedule here.
          </Link>
        </div>
      }
    />
  )
}
